import {
  DirectionsBike,
  DirectionsRun,
  Home as IndoorIcon,
  Park as OutdoorIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Workout } from '@shared/types';
import React from 'react';
import { formatCalendarDate, formatDayOfWeek, formatDistance } from '../../utils/dateUtils';
import { calculateTrainingSummary } from '../../utils/workoutUtils';

interface WorkoutCardProps {
  date: Date;
  workout: Workout | null;
  isToday: boolean;
  isSwappable?: boolean;
  isSelected?: boolean;
  isSimplified?: boolean;
  onClick?: () => void;
  getWorkoutTypeColor: (workoutName: string) => string;
  healthStatus?: 'normal' | 'sick' | 'injured';
}

export const WorkoutCard: React.FC<WorkoutCardProps> = ({
  date,
  workout,
  isToday,
  isSelected = false,
  isSimplified = false,
  onClick,
  getWorkoutTypeColor,
  healthStatus = 'normal',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isRest = !workout || (workout.type || '').toLowerCase() === 'rest';
  const isClickable = Boolean(onClick); // Allow clicking on rest days too

  // Check if date is in the past (before today)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
  const cardDate = new Date(date);
  cardDate.setHours(0, 0, 0, 0);
  const isPastDate = cardDate < today;

  // Check if this is a future date and user is sick/injured
  const isFutureDate = cardDate >= today;
  const isSickOrInjured = healthStatus === 'sick' || healthStatus === 'injured';
  const shouldGreyOut = isFutureDate && isSickOrInjured;

  // The getWorkoutTypeColor function returns just the primary color string
  // Use grey for rest days for better visual differentiation
  const workoutColor = workout ? getWorkoutTypeColor(workout.name || '') : '#2196F3';

  return (
    <Card
      elevation={isToday ? 4 : isSelected ? 3 : 1}
      sx={{
        height: isSimplified ? { xs: 40, sm: 80 } : { xs: 60, sm: 120, md: 85 },
        cursor: isClickable && (workout || isRest) ? 'pointer' : 'default',
        opacity: shouldGreyOut ? 0.3 : isPastDate ? 0.5 : isRest ? 0.2 : 1,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        border: isSelected
          ? '3px solid #1976d2'
          : isToday
            ? `2px solid ${workout ? workoutColor : '#ff6b35'}`
            : workout && !isRest
              ? `1px solid ${workoutColor}`
              : '1px solid rgba(0, 0, 0, 0.08)',
        bgcolor: isToday
          ? workout && !isRest
            ? `${workoutColor}20` // 20% opacity of workout color for today
            : 'primary.50'
          : isRest
            ? 'grey.100' // Greyed out background for rest days
            : workout
              ? `${workoutColor}08` // 8% opacity of workout color for other days
              : 'background.paper',
        position: 'relative',
        overflow: 'visible',
        ...(isToday && {
          boxShadow: `0 3px 15px ${workout ? workoutColor : '#ff6b35'}40`, // Glowing shadow for today
          transform: 'translateY(-1px)',
        }),
        ...(isClickable && !isRest
          ? {
              '&:hover': {
                elevation: isToday ? 6 : 4,
                transform: isToday ? 'translateY(-2px)' : 'translateY(-1px)',
                transition: 'all 0.3s ease-in-out',
                boxShadow: isToday
                  ? `0 4px 20px ${workout ? workoutColor : '#ff6b35'}60`
                  : `0 3px 12px ${workout ? workoutColor : 'rgba(0,0,0,0.15)'}40`,
              },
            }
          : {}),
        transition: 'all 0.3s ease-in-out',
      }}
      onClick={() => {
        if (!isClickable) {
          return;
        }
        // Allow clicking on rest days (workout might be null or have name 'Rest')
        if (workout || isRest) {
          onClick?.();
        }
      }}
    >
      <CardContent
        sx={{
          p: { xs: 0.5, sm: 0.75 },
          '&:last-child': { pb: { xs: 0.5, sm: 0.75 } },
          overflow: 'hidden',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Today indicator badge */}
        {isToday && (
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              right: -8,
              width: 16,
              height: 16,
              borderRadius: '50%',
              bgcolor: workout ? workoutColor : '#ff6b35',
              border: '2px solid white',
              zIndex: 1,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', opacity: 1 },
                '50%': { transform: 'scale(1.1)', opacity: 0.7 },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        )}

        {/* Mobile Layout: Left-Center-Right */}
        {isMobile && !isSimplified ? (
          <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
            {/* Left: Day of week and date */}
            <Box display="flex" flexDirection="column" alignItems="flex-start" flex="0 0 auto">
              <Typography
                variant="caption"
                sx={{
                  fontSize: '1rem',
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isToday ? (workout && !isRest ? workoutColor : '#ff6b35') : '#949494ff',
                  lineHeight: 1,
                }}
              >
                {formatDayOfWeek(date).slice(0, 3)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '2rem',
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isToday ? (workout && !isRest ? workoutColor : '#ff6b35') : '#949494ff',
                  lineHeight: 1,
                }}
              >
                {formatCalendarDate(date)}
              </Typography>
            </Box>

            {/* Center: Sport Icon */}
            {workout && !isRest && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                flex="0 0 auto"
                mx={0.5}
              >
                {workout.sportType === 'cycling' ? (
                  <DirectionsBike
                    sx={{
                      fontSize: '2.5rem',
                      color: workoutColor,
                      opacity: 0.7,
                    }}
                  />
                ) : (
                  <DirectionsRun
                    sx={{
                      fontSize: '2.5rem',
                      color: workoutColor,
                      opacity: 0.7,
                    }}
                  />
                )}
                {/* Indoor/Outdoor indicator */}
                {workout.environment === 'indoor' ? (
                  <Tooltip title="Indoor" arrow>
                    <IndoorIcon
                      sx={{
                        fontSize: '1rem',
                        color: 'text.secondary',
                        opacity: 0.7,
                        mt: -0.5,
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Outdoor" arrow>
                    <OutdoorIcon
                      sx={{
                        fontSize: '1rem',
                        color: 'success.main',
                        opacity: 0.7,
                        mt: -0.5,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Center: Training details */}
            <Box display="flex" flexDirection="column" alignItems="center" flex="1" mx={1}>
              {workout ? (
                <>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      variant="subtitle2"
                      align="center"
                      sx={{
                        fontSize: '1.3rem',
                        fontWeight: 'bold',
                        color: workoutColor,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        lineHeight: 1.1,
                      }}
                      title={workout.name}
                    >
                      {workout.name}
                    </Typography>
                  </Box>

                  {/* Training Summary */}
                  <Typography
                    variant="caption"
                    align="center"
                    color="text.secondary"
                    sx={{
                      fontSize: '1.0rem',
                      lineHeight: 1.1,
                      mt: 0.3,
                      fontStyle: 'italic',
                    }}
                  >
                    {calculateTrainingSummary(workout)}
                  </Typography>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Typography
                    variant="caption"
                    align="center"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'italic',
                      opacity: 0.5,
                      fontSize: '0.65rem',
                    }}
                  >
                    No Workout
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right: TSS chip */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flex="0 0 auto"
            >
              {workout && workout.TSS && (
                <Chip
                  label={
                    <span style={{ display: 'inline-block', textAlign: 'center', lineHeight: 1 }}>
                      <span style={{ display: 'block' }}>{Math.round(workout.TSS)}</span>
                      <span style={{ display: 'block', fontSize: '0.65em' }}>TSS</span>
                    </span>
                  }
                  size="small"
                  variant={isToday ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '1.2rem',
                    height: 38,
                    minWidth: 35,
                    bgcolor: isToday ? `${workoutColor}20` : 'transparent',
                    borderColor: workoutColor,
                    color: workoutColor,
                    fontWeight: isToday ? 'bold' : 'normal',
                    '& .MuiChip-label': {
                      px: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      lineHeight: 1,
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        ) : (
          /* Desktop/Simplified Layout: 2-row grid layout */
          <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between">
            {/* Row 1: Date (left) | Icon (center) | Workout Type (right) */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              mb={0.25}
              sx={{ minWidth: 0 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: { xs: '0.6rem', sm: '0.9rem' },
                  fontWeight: isToday ? 'bold' : 'normal',
                  color: isToday
                    ? workout && !isRest
                      ? workoutColor
                      : '#ff6b35'
                    : 'text.secondary',
                  textShadow: isToday ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                }}
              >
                {isMobile || isSmallScreen
                  ? `${formatCalendarDate(date)}`
                  : `${formatDayOfWeek(date)} ${formatCalendarDate(date)}`}
              </Typography>

              {workout && !isRest && (
                <Box display="flex" alignItems="center" justifyContent="center" gap={0.3}>
                  {workout.sportType === 'cycling' ? (
                    <DirectionsBike
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '1.2rem' },
                        color: workoutColor,
                        opacity: 0.6,
                      }}
                    />
                  ) : (
                    <DirectionsRun
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '1.2rem' },
                        color: workoutColor,
                        opacity: 0.6,
                      }}
                    />
                  )}
                  {workout.environment === 'indoor' ? (
                    <Tooltip title="Indoor" arrow>
                      <IndoorIcon
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.9rem' },
                          color: 'text.secondary',
                          opacity: 0.7,
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Outdoor" arrow>
                      <OutdoorIcon
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.9rem' },
                          color: 'success.main',
                          opacity: 0.7,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}

              {workout && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.3}
                  sx={{
                    minWidth: 0,
                    flex: '1 1 0',
                    justifyContent: 'flex-end',
                    overflow: 'hidden',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: '0.55rem', sm: '1rem' },
                      fontWeight: 'bold',
                      color: workoutColor,
                      textAlign: 'right',
                      textShadow: isToday ? '0 1px 2px rgba(0,0,0,0.2)' : 'none',
                      lineHeight: 1.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                      ...(isToday && {
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                      }),
                    }}
                    title={workout.name}
                  >
                    {isSmallScreen && !isMobile && workout.name.length > 3
                      ? workout.name.slice(0, 3) + '...'
                      : workout.name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Row 2: TSS (left) | empty (center) | Distance & Summary (right) */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt={0.5}>
              {/* TSS chip on the left */}
              {workout && workout.TSS && !isSimplified ? (
                <Chip
                  label={`${Math.round(workout.TSS)}`}
                  size="small"
                  variant={isToday ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: { xs: '0.5rem', sm: '1.2rem' },
                    height: { xs: 14, sm: 25 },
                    minWidth: { xs: 20, sm: 24 },
                    bgcolor: isToday ? `${workoutColor}20` : 'transparent',
                    borderColor: workoutColor,
                    color: workoutColor,
                    fontWeight: isToday ? 'bold' : 'normal',
                    '& .MuiChip-label': {
                      px: 0.4,
                      py: 0,
                      lineHeight: 1,
                    },
                  }}
                />
              ) : (
                <Box /> // Empty placeholder for alignment
              )}

              {/* Distance and Summary on the right */}
              <Box>
                {workout ? (
                  <>
                    {!isSimplified &&
                      workout.environment !== 'indoor' &&
                      workout.total_distance &&
                      workout.total_distance > 0 && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{
                            fontSize:
                              workout.name === 'Long Ride'
                                ? { xs: '0.75rem', sm: '0.9rem' }
                                : { xs: '0.55rem', sm: '0.65rem' },
                            fontWeight:
                              workout.name === 'Long Ride' ? 'bold' : isToday ? 'medium' : 'normal',
                            mb: 0.25,
                            textAlign: 'right',
                          }}
                        >
                          {formatDistance(workout.total_distance)}
                        </Typography>
                      )}

                    {!isSimplified && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.5rem', sm: '0.6rem' },
                          fontStyle: 'italic',
                          lineHeight: 1.1,
                          textAlign: 'right',
                        }}
                      >
                        {calculateTrainingSummary(workout)}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{
                      fontStyle: 'italic',
                      opacity: 0.5,
                      fontSize: { xs: '0.55rem', sm: '0.65rem' },
                      textAlign: 'right',
                    }}
                  >
                    No Workout
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
